import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Descriptions, Divider, Skeleton, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n, type MessageKey } from "@/i18n";

// TikTok's fee names Zerpid translates; any other shows as TikTok names it.
const feeNames: Record<string, MessageKey> = {
  platform_commission: "ar.fee.platform_commission",
  transaction_fee: "ar.fee.transaction_fee",
  affiliate_commission: "ar.fee.affiliate_commission",
  referral_fee: "ar.fee.referral_fee",
  sfp_service_fee: "ar.fee.sfp_service_fee",
  voucher_xtra_service_fee: "ar.fee.voucher_xtra_service_fee",
  flash_sales_service_fee: "ar.fee.flash_sales_service_fee",
  live_specials_fee: "ar.fee.live_specials_fee",
  vat: "ar.fee.vat",
};

/**
 * How the order's price was made and what the marketplace kept, read from the
 * marketplace only when asked (each read spends the store's API quota).
 */
export function OrderMoney({ orderId, tz }: { orderId: string; tz?: string }) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const errorText = useErrorText();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["finance", "orderMoney", orderId],
    queryFn: () => unwrap(api.GET("/v1/finance/receivables/orders/{orderID}/money", { params: { path: { orderID: orderId } } })),
    enabled: open,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const money = (n: number) => <span className="z-num">{formatMoney(n)}</span>;
  const minus = (n: number) => (n ? money(-n) : "—");

  let body;
  if (!open) {
    body = (
      <Button size="small" onClick={() => setOpen(true)}>
        {t("orders.money.show")}
      </Button>
    );
  } else if (q.isPending) {
    body = <Skeleton active paragraph={{ rows: 4 }} title={false} />;
  } else if (q.isError) {
    body = <Alert type="warning" showIcon title={errorText(q.error)} />;
  } else {
    const { price, settlement, estimate } = q.data;
    body = (
      <>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t("orders.money.listPrice")}>{money(price.listPrice)}</Descriptions.Item>
          <Descriptions.Item label={t("orders.money.sellerDiscount")}>{minus(price.sellerDiscount)}</Descriptions.Item>
          <Descriptions.Item label={t("orders.money.platformDiscount")}>{minus(price.platformDiscount)}</Descriptions.Item>
          <Descriptions.Item label={t("orders.money.shippingFee")}>{money(price.shippingFee)}</Descriptions.Item>
          <Descriptions.Item label={t("orders.money.shippingSeller")}>{minus(price.shippingSeller)}</Descriptions.Item>
          <Descriptions.Item label={t("orders.money.shippingPlatform")}>{minus(price.shippingPlatform)}</Descriptions.Item>
          {price.tax !== 0 && <Descriptions.Item label={t("orders.money.tax")}>{money(price.tax)}</Descriptions.Item>}
          <Descriptions.Item label={<Typography.Text strong>{t("orders.money.buyerPaid")}</Typography.Text>}>
            <Typography.Text strong className="z-num">
              {formatMoney(price.buyerPaid)}
            </Typography.Text>
          </Descriptions.Item>
        </Descriptions>
        <Divider style={{ margin: "8px 0" }} />
        {settlement ? (
          <Descriptions column={1} size="small">
            <Descriptions.Item label={t("ar.revenue")}>{money(settlement.revenue)}</Descriptions.Item>
            {settlement.feeLines.map((f) => (
              <Descriptions.Item key={f.name} label={feeNames[f.name] ? t(feeNames[f.name]!) : f.name.replaceAll("_", " ")}>
                {money(f.amount)}
              </Descriptions.Item>
            ))}
            {settlement.shipping !== 0 && <Descriptions.Item label={t("orders.money.shippingCost")}>{money(settlement.shipping)}</Descriptions.Item>}
            <Descriptions.Item label={<Typography.Text strong>{t("orders.money.received")}</Typography.Text>}>
              <Typography.Text strong className="z-num">
                {formatMoney(settlement.net)}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        ) : estimate ? (
          <Typography.Text type="secondary">
            {t("orders.money.estimate", { amount: formatMoney(estimate.net) })}{" "}
            {estimate.releaseAt ? t("orders.money.estimateAt", { at: formatDateTime(estimate.releaseAt, tz) }) : estimate.reason}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">{t("orders.money.notSettled")}</Typography.Text>
        )}
      </>
    );
  }
  return (
    <Card size="small" title={t("orders.money.title")}>
      {body}
    </Card>
  );
}
